import PropTypes from 'prop-types';
import {useTranslations} from 'next-intl';
import {H4, H3} from '@/typography';
import ToggleButton, {TOGGLE_BUTTON_VARIANTS} from '@/form-fields/toggle-button';
import Money from '@/ui/money';
import cx from 'classnames';
import {ListNeutral900} from '@/ui/lists/list-icons';
import TooltipText from '@/ui/tooltip-text';
import Tooltip from '@/ui/tooltip';
import Pill from '@/ui/pills';
import Link from '@/links';
import {THOUSAND_PLUS, PLAN_YEARLY, PLAN_MONTHLY} from '@/content/pricing/plans-section/plan.const';
import ScrollingHandler from '@/scrollinghandler';

// Styles
import {CheckmarkSVG} from '@/svg/index';
import styles from './styles.module.scss';

const prepareLink = ScrollingHandler.prepareOnClick;

const showFeatures = features =>
  features.map((feature, index) => (
    <li
      key={`feature_${index}`}
      className={cx({[styles['plan__feature-list-crossed']]: feature?.included === false})}>
      {feature.tooltip ? (
        <div className={styles['plan__feature-list-tooltip']}>
          {feature.name}
          <Tooltip text={feature.tooltip} />
        </div>
      ) : (
        feature.name
      )}
    </li>
  ));

export const getPeriodPrice = (price, billingCycle) =>
  typeof price === 'number' ? price : price?.[billingCycle];

const planBillingCyclePropTypes = {
  plan: PropTypes.object,
  setBillingCycle: PropTypes.func.isRequired,
  companySize: PropTypes.string
};

export const PlanBillingCycle = ({plan, setBillingCycle, companySize}) => {
  const t = useTranslations('pricing.plansSection.plan');

  return (
    <div className={styles['plan__billing-cycle']}>
      {plan?.monthly_only && (
        <div
          className={cx(
            styles['plan__dropdown-skeleton'],
            styles['plan__dropdown-skeleton--solid']
          )}>
          {t('payMonthly')}
        </div>
      )}
      {plan?.yearly_only && (
        <div
          className={cx(
            styles['plan__dropdown-skeleton'],
            styles['plan__dropdown-skeleton--solid']
          )}>
          {t('payAnnually')}
        </div>
      )}
      {!plan?.monthly_only && !plan?.yearly_only && (
        <div
          className={cx({
            [styles['plan__period-inner']]: true,
            [styles['plan__period-inner-hide']]: companySize === THOUSAND_PLUS
          })}>
          <ToggleButton
            checked
            disabled={companySize === THOUSAND_PLUS}
            name="billing_cycle"
            values={[
              {
                text: <>{t('monthly').toUpperCase()}</>,
                value: PLAN_MONTHLY
              },
              {
                text: <>{t('annuallySave20').toUpperCase()}</>,
                value: PLAN_YEARLY
              }
            ]}
            onChange={checked => setBillingCycle(checked.value)}
          />
        </div>
      )}
    </div>
  );
};

PlanBillingCycle.propTypes = planBillingCyclePropTypes;

const planPremiumToolsPropTypes = {
  plan: PropTypes.object.isRequired,
  premium_tools: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  companySize: PropTypes.string,
  currency: PropTypes.string,
  billingCycle: PropTypes.string
};

export const PlanPremiumTools = ({
  plan,
  premium_tools,
  disabled,
  companySize,
  currency,
  billingCycle
}) => {
  const t = useTranslations('pricing.plansSection.plan');
  const pricesKeys = Object.keys(plan.prices);

  const getPremiumToolPrice = premiumTool =>
    typeof premiumTool.price === 'number'
      ? getPeriodPrice(premiumTool.price, billingCycle)
      : getPeriodPrice(premiumTool.price[companySize], billingCycle);

  return (
    <div className={styles['plan__premium-tools']}>
      <H4>{plan?.addons_included ? t('included') : t('addons')}</H4>
      <ListNeutral900
        className={cx(styles['plan__feature-list'], {
          [styles['plan__feature-list--crossed']]: disabled
        })}>
        {premium_tools.map((premiumTool, index) => (
          <li
            key={`${plan.name}_${index}`}
            className={cx({
              [styles['plan__feature-list-crossed']]: premiumTool?.included === false
            })}>
            <span>
              <span className={styles['plan__premium-tools-label']}>
                {premiumTool.href ? (
                  <Link
                    href={premiumTool.href}
                    className={styles['plan__premium-tools-link']}>
                    {premiumTool.name}
                  </Link>
                ) : (
                  premiumTool.name
                )}
                {premiumTool.label === 'new' && (
                  <Pill variant="ai" disabled={disabled || premiumTool?.included === false}>
                    {t('new')}
                  </Pill>
                )}
              </span>
              {premiumTool.included && (
                <span>
                  <CheckmarkSVG active={!disabled} />
                </span>
              )}
              {premiumTool.note && (
                <span className={styles['plan__premium-tools-note']}>
                  {premiumTool.note}
                </span>
              )}
              {(companySize !== THOUSAND_PLUS || pricesKeys.length === 1) &&
                premiumTool?.price &&
                !premiumTool.included && (
                  <span>
                    <span className="plan__premium-tools-plus">+</span>{' '}
                    <Money
                      currency={currency}
                      animate
                      className={styles['plan__premium-tools-price']}
                      value={getPremiumToolPrice(premiumTool)}
                      key={getPremiumToolPrice(premiumTool)}
                    />
                    <em>{t('perMonth')}</em>
                  </span>
                )}
            </span>
          </li>
        ))}
      </ListNeutral900>
    </div>
  );
};

PlanPremiumTools.propTypes = planPremiumToolsPropTypes;

const planRecruitingAddonsPropTypes = {
  plan: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  companySize: PropTypes.string,
  currency: PropTypes.string,
  billingCycle: PropTypes.string
};

export const PlanRecruitingAddons = ({
  plan,
  disabled,
  companySize,
  currency,
  billingCycle
}) => {
  const t = useTranslations('pricing.plansSection.plan');
  const addons = plan?.recruiting_addons || [];
  const pricesKeys = Object.keys(plan?.prices || {});

  const getAddonPrice = addon =>
    typeof addon.price === 'number'
      ? getPeriodPrice(addon.price, billingCycle)
      : getPeriodPrice(addon.price?.[companySize], billingCycle);

  if (!addons.length) {
    return null;
  }

  return (
    <div className={styles['plan__premium-tools']}>
      <H4>{t('addons')}</H4>
      <ListNeutral900
        className={cx(styles['plan__feature-list'], {
          [styles['plan__feature-list--crossed']]: disabled
        })}>
        {addons.map((addon, index) => (
          <li key={`${plan.name}_addon_${index}`}>
            <span>
              <span className={styles['plan__premium-tools-label']}>
                {addon.href ? (
                  <Link
                    href={addon.href}
                    className={styles['plan__premium-tools-link']}>
                    {addon.name}
                  </Link>
                ) : (
                  <span>{addon.name}</span>
                )}
                {addon.label === 'new' && (
                  <Pill variant="ai" disabled={disabled}>
                    {t('new')}
                  </Pill>
                )}
              </span>
              {addon.note && (
                <span className={styles['plan__premium-tools-note']}>
                  {addon.note}
                </span>
              )}
              {(companySize !== THOUSAND_PLUS || pricesKeys.length === 1) &&
                addon?.price &&
                getAddonPrice(addon) != null && (
                  <span>
                    <span className="plan__premium-tools-plus">+</span>{' '}
                    <Money
                      currency={currency}
                      animate
                      className={styles['plan__premium-tools-price']}
                      value={getAddonPrice(addon)}
                      key={getAddonPrice(addon)}
                    />
                    <em>{t('perMonth')}</em>
                  </span>
                )}
            </span>
          </li>
        ))}
      </ListNeutral900>
    </div>
  );
};

PlanRecruitingAddons.propTypes = planRecruitingAddonsPropTypes;

const planServicesPropTypes = {
  plan: PropTypes.object.isRequired
};


export const PlanServices = ({plan}) => {
  const t = useTranslations('pricing.plansSection');
  const planT = useTranslations('pricing.plansSection.plan');
  const services = t.raw('services');

  return (
    <div
      className={cx(styles.plan__box, styles['plan__box--services'])}>
      <H3>{planT('advancedFeaturesServices')}</H3>
      <ListNeutral900 className={styles['plan__feature-list']}>
        {services.map((serviceFeature, index) => (
          <li
            key={`${plan.name}_services_${index}`}
            className={cx({[styles['plan__feature-list-crossed']]: !plan?.services_included})}>
            {serviceFeature}
          </li>
        ))}
      </ListNeutral900>
    </div>
  );
};

PlanServices.propTypes = planServicesPropTypes;

const planFreeFeaturesPropTypes = {
  plan: PropTypes.object.isRequired
};

export const PlanFreeFeatures = ({plan}) => {
  const t = useTranslations('pricing.plansSection');
  const planT = useTranslations('pricing.plansSection.plan');
  const freeFeatures = t.raw('freeFeatures');

  return (
    <div className={cx(styles.plan__box, styles['plan__box--free'])}>
      <H3>{planT('includedInAllPlans')}</H3>
      <ListNeutral900 className={styles['plan__feature-list']}>
        {freeFeatures.map((freeFeature, index) => (
          <li key={`${plan.name}_feature_${index}`}>{freeFeature}</li>
        ))}
      </ListNeutral900>
    </div>
  );
};

PlanFreeFeatures.propTypes = planFreeFeaturesPropTypes;

const AGENT_ADDONS = ['sourcingAgent', 'screeningAgent', 'engagementAgent'];

const planWorkableAgentPropTypes = {
  disabled: PropTypes.bool
};

export const PlanWorkableAgent = ({disabled = false}) => {
  const t = useTranslations('pricing.plansSection.plan.workableAgent');
  const planT = useTranslations('pricing.plansSection.plan');

  return (
    <div className={styles.plan__agent}>
      <div className={styles['plan__agent-card']}>
        <div className={styles['plan__agent-header']}>
          <H4 className={styles['plan__agent-title']}>{t('title')}</H4>
          <Pill variant="ai" disabled={disabled} className={styles['plan__agent-pill']}>
            {planT('new')}
          </Pill>
        </div>
        <div className={styles['plan__agent-included']}>
          <span>{t('jobBriefAgent')}</span>
          <span className={styles['plan__agent-meta']}>{t('included')}</span>
        </div>
        <div className={styles['plan__agent-addons']}>
          <p className={styles['plan__agent-addons-title']}>{t('addons')}</p>
          <ul className={styles['plan__agent-addons-list']}>
            {AGENT_ADDONS.map(agent => (
              <li key={agent}>
                <span>{t(agent)}</span>
                <span className={styles['plan__agent-meta']}>{t('usesAiCredits')}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <p className={styles['plan__agent-note']}>
        {t.rich('creditsNote', {
          link: chunks => (
            <Link
              href={t('creditsHref')}
              onClick={prepareLink(t('creditsHref'))}
              className={styles['plan__agent-note-link']}>
              {chunks}
            </Link>
          )
        })}
      </p>
    </div>
  );
};

PlanWorkableAgent.propTypes = planWorkableAgentPropTypes;

const planRecruitingBoxPropTypes = {
  plan: PropTypes.object.isRequired,
  isRecruitingEnabled: PropTypes.bool.isRequired,
  setIsRecruitingEnabled: PropTypes.func.isRequired,
  isHREnabled: PropTypes.bool.isRequired,
  companySize: PropTypes.string,
  currency: PropTypes.string,
  billingCycle: PropTypes.string,
  recruitingToggleRef: PropTypes.object
};

export const PlanRecruitingBox = ({
  plan,
  isRecruitingEnabled,
  setIsRecruitingEnabled,
  isHREnabled,
  companySize,
  currency,
  billingCycle,
  recruitingToggleRef
}) => {
  const t = useTranslations('pricing.plansSection.plan');
  const disabled = !isRecruitingEnabled && plan?.recruiting_enabled;
  const disabledToggleButton = !isHREnabled && plan?.hr_enabled;

  return (
    <div
      className={cx(
        styles.plan__box,
        styles['plan__box--featured'],
        styles['plan__box--recruiting'],
        {
          [styles['plan__box--disabled']]: disabled
        }
      )}>
      {!plan?.recruiting_enabled ? (
        <H3>{t('recruiting')}</H3>
      ) : (
        <>
          {disabledToggleButton && (
            <TooltipText anchorSelect={`.${plan.name}-recruiting-toggle`}>
              {t('atLeastOneProductSelected')}
            </TooltipText>
          )}
          <ToggleButton
            onChange={option => setIsRecruitingEnabled(option.value)}
            checked={isRecruitingEnabled}
            ref={recruitingToggleRef}
            variant={TOGGLE_BUTTON_VARIANTS.NO_TEXT}
            values={[
              {text: '', value: false},
              {text: '', value: true}
            ]}
            className={cx(`${plan.name}-recruiting-toggle`, styles['plan__box-toggle-button'])}
            infoText={t('recruiting')}
          />
        </>
      )}

      <ListNeutral900
        className={cx(styles['plan__feature-list'], {
          [styles['plan__feature-list--crossed']]: disabled
        })}>
        {showFeatures(plan.features)}
      </ListNeutral900>

      <PlanPremiumTools
        premium_tools={plan.recruiting_premium_tools}
        {...{plan, disabled, companySize, currency, billingCycle}}
      />
      <PlanRecruitingAddons
        plan={plan}
        disabled={disabled}
        companySize={companySize}
        currency={currency}
        billingCycle={billingCycle}
      />
      <PlanWorkableAgent disabled={disabled} />
    </div>
  );
};

PlanRecruitingBox.propTypes = planRecruitingBoxPropTypes;

const planHRBoxPropTypes = {
  plan: PropTypes.object.isRequired,
  isRecruitingEnabled: PropTypes.bool.isRequired,
  setIsHREnabled: PropTypes.func.isRequired,
  isHREnabled: PropTypes.bool.isRequired,
  hrToggleRef: PropTypes.object,
  companySize: PropTypes.string,
  currency: PropTypes.string,
  billingCycle: PropTypes.string
};

export const PlanHRBox = ({
  plan,
  isRecruitingEnabled,
  setIsHREnabled,
  isHREnabled,
  hrToggleRef,
  companySize,
  currency,
  billingCycle
}) => {
  const t = useTranslations('pricing.plansSection.plan');
  const disabled = !plan?.hr_enabled || !isHREnabled;
  const disabledToggleButton = !isRecruitingEnabled && plan?.recruiting_enabled;

  return (
    <div
      className={cx(styles.plan__box, styles['plan__box--featured'], {
        [styles['plan__box--disabled']]: disabled
      })}>
      {!plan?.hr_enabled ? (
        <H3>{t('hr')}</H3>
      ) : (
        <>
          {disabledToggleButton && (
            <TooltipText anchorSelect={`.${plan.name}-hr-toggle`}>
              {t('atLeastOneProductSelected')}
            </TooltipText>
          )}
          <ToggleButton
            onChange={option => setIsHREnabled(option.value)}
            checked={isHREnabled}
            ref={hrToggleRef}
            variant={TOGGLE_BUTTON_VARIANTS.NO_TEXT}
            values={[
              {text: '', value: false},
              {text: '', value: true}
            ]}
            className={cx(`${plan.name}-hr-toggle`, styles['plan__box-toggle-button'])}
            infoText={t('hr')}
          />
        </>
      )}

      <ListNeutral900
        className={cx(styles['plan__feature-list'], {
          [styles['plan__feature-list--crossed']]: disabled
        })}>
        {showFeatures(plan.hr_features)}
      </ListNeutral900>

      <PlanPremiumTools
        premium_tools={plan.hr_premium_tools}
        {...{plan, disabled, companySize, currency, billingCycle}}
      />
    </div>
  );
};

PlanHRBox.propTypes = planHRBoxPropTypes;
